import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';
import { formatDate, formatTime } from '../utils/dates';

const inp = { background: 'var(--bg-primary)', border: '1px solid var(--border)', borderRadius: 4, color: 'var(--text-primary)', padding: '7px 11px', fontFamily: 'var(--font-body)', fontSize: 13 };

// ─── Eastern Time <-> datetime-local helpers ───────────────────────────────
// datetime-local inputs carry a naive "YYYY-MM-DDTHH:mm" string with no zone
// info. We always want that string to represent Eastern wall-clock time,
// regardless of the viewer's device timezone — consistent with formatTime's
// use of America/New_York everywhere else in this app.
const EASTERN_PARTS = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', hour12: false,
  year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit',
});

function isoToEasternInput(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const parts = EASTERN_PARTS.formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  const hh = parts.hour === '24' ? '00' : parts.hour;
  return `${parts.year}-${parts.month}-${parts.day}T${hh}:${parts.minute}`;
}

// Converts a "YYYY-MM-DDTHH:mm" string, interpreted as Eastern wall-clock
// time, to a UTC ISO instant — correctly handling EST/EDT.
function easternInputToISO(value) {
  if (!value) return null;
  const [datePart, timePart] = value.split('T');
  const [y, m, d] = datePart.split('-').map(Number);
  const [hh, mm] = timePart.split(':').map(Number);
  const guess = Date.UTC(y, m - 1, d, hh, mm);
  const parts = EASTERN_PARTS.formatToParts(new Date(guess)).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
  const shownAsEastern = Date.UTC(
    +parts.year, +parts.month - 1, +parts.day,
    parts.hour === '24' ? 0 : +parts.hour, +parts.minute
  );
  const diff = shownAsEastern - guess;
  return new Date(guess - diff).toISOString();
}

function defaultRange() {
  const to = new Date().toISOString().slice(0, 10);
  const from = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  return { from, to };
}

function DateRange({ from, to, onFrom, onTo }) {
  return (
    <>
      <input type="date" value={from} onChange={e => onFrom(e.target.value)} style={inp} />
      <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>to</span>
      <input type="date" value={to} onChange={e => onTo(e.target.value)} style={inp} />
    </>
  );
}

// ─── Clock Sessions tab ─────────────────────────────────────────────────────
function ClockSessionRow({ row, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ started_at: '', ended_at: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const startEdit = () => {
    setForm({ started_at: isoToEasternInput(row.started_at), ended_at: isoToEasternInput(row.ended_at) });
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    if (!form.started_at || !form.ended_at) return setError('Both times are required');
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/clock-sessions/${row.id}`, {
        started_at: easternInputToISO(form.started_at),
        ended_at: easternInputToISO(form.ended_at),
      });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
    setSaving(false);
  };

  const forceOff = async () => {
    if (!window.confirm(`Force clock-off ${row.staff_name}?`)) return;
    try {
      await api.delete(`/clock-sessions/${row.id}`);
      onSaved();
    } catch (err) {
      window.alert(err.response?.data?.error || err.message);
    }
  };

  const del = async () => {
    if (!window.confirm('Permanently delete this session? This cannot be undone.')) return;
    try {
      await api.delete(`/clock-sessions/${row.id}?hard=true`);
      onSaved();
    } catch (err) {
      window.alert(err.response?.data?.error || err.message);
    }
  };

  return (
    <tr>
      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{formatDate(row.started_at)}</td>
      <td>{row.staff_name}</td>
      <td>{row.project_title || '—'}</td>
      <td>{row.work_centre_name}</td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
        {editing
          ? <input type="datetime-local" value={form.started_at} onChange={e => setForm(f => ({ ...f, started_at: e.target.value }))} style={inp} />
          : formatTime(row.started_at)}
      </td>
      <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
        {editing
          ? <input type="datetime-local" value={form.ended_at} onChange={e => setForm(f => ({ ...f, ended_at: e.target.value }))} style={inp} />
          : (row.ended_at ? formatTime(row.ended_at) : '—')}
      </td>
      <td>{row.duration_minutes != null ? (row.duration_minutes / 60).toFixed(2) : '—'}</td>
      <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>clock</td>
      <td>
        {editing ? (
          <div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
            {error && <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 4, maxWidth: 220 }}>{error}</div>}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            <button className="btn btn-secondary btn-sm" onClick={startEdit}>Edit</button>
            {!row.ended_at && <button className="btn btn-secondary btn-sm" onClick={forceOff}>Force Clock-Off</button>}
            <button className="btn btn-danger btn-sm" onClick={del}>Delete</button>
          </div>
        )}
      </td>
    </tr>
  );
}

function ClockSessionsTab({ projects, staff }) {
  const dr = defaultRange();
  const [from, setFrom] = useState(dr.from);
  const [to, setTo] = useState(dr.to);
  const [staffFilter, setStaffFilter] = useState('');
  const [projFilter, setProjFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ date_from: from, date_to: to });
    if (staffFilter) p.set('staff_id', staffFilter);
    if (projFilter) p.set('project_id', projFilter);
    api.get(`/clock-sessions/all?${p}`)
      .then(r => { setRows(r.data.value || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [from, to, staffFilter, projFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <select value={staffFilter} onChange={e => setStaffFilter(e.target.value)} style={inp}>
          <option value="">All Staff</option>
          {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={projFilter} onChange={e => setProjFilter(e.target.value)} style={inp}>
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.title}</option>)}
        </select>
      </div>
      <div className="card">
        {loading ? (
          <div className="loading">Loading…</div>
        ) : !rows.length ? (
          <div className="empty-state">No sessions for the selected filters</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>Staff</th><th>Project</th><th>Work Centre</th>
                  <th>Start</th><th>End</th><th>Hours</th><th>Source</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => <ClockSessionRow key={row.id} row={row} onSaved={load} />)}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Manual Entries tab ─────────────────────────────────────────────────────
function ManualLogRow({ row, projects, staff, workCentres, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const startEdit = () => {
    setForm({
      log_date: String(row.log_date).slice(0, 10),
      project_id: row.project_id || '',
      staff_id: row.staff_id || '',
      work_centre_id: row.work_centre_id || '',
      hours_decimal: row.hours_decimal,
      notes: row.notes || '',
    });
    setError(null);
    setEditing(true);
  };

  const save = async () => {
    if (!form.hours_decimal || parseFloat(form.hours_decimal) <= 0) return setError('Hours must be greater than 0');
    setSaving(true);
    setError(null);
    try {
      await api.patch(`/production-logs/${row.id}`, {
        log_date: form.log_date,
        hours_decimal: parseFloat(form.hours_decimal),
        project_id: form.project_id ? parseInt(form.project_id) : null,
        staff_id: form.staff_id ? parseInt(form.staff_id) : null,
        work_centre_id: form.work_centre_id ? parseInt(form.work_centre_id) : null,
        notes: form.notes,
      });
      setEditing(false);
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
    setSaving(false);
  };

  const del = async () => {
    if (!window.confirm('Delete this log entry?')) return;
    try {
      await api.delete(`/production-logs/${row.id}`);
      onSaved();
    } catch (err) {
      window.alert(err.response?.data?.error || err.message);
    }
  };

  if (editing) {
    return (
      <tr>
        <td><input type="date" value={form.log_date} onChange={e => setForm(f => ({ ...f, log_date: e.target.value }))} style={inp} /></td>
        <td>
          <select value={form.staff_id} onChange={e => setForm(f => ({ ...f, staff_id: e.target.value }))} style={inp}>
            <option value="">—</option>
            {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </td>
        <td>
          <select value={form.project_id} onChange={e => setForm(f => ({ ...f, project_id: e.target.value }))} style={inp}>
            <option value="">—</option>
            {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.title}</option>)}
          </select>
        </td>
        <td>
          <select value={form.work_centre_id} onChange={e => setForm(f => ({ ...f, work_centre_id: e.target.value }))} style={inp}>
            <option value="">—</option>
            {workCentres.map(wc => <option key={wc.id} value={wc.id}>{wc.name}</option>)}
          </select>
        </td>
        <td><input type="number" step="0.25" min="0" value={form.hours_decimal} onChange={e => setForm(f => ({ ...f, hours_decimal: e.target.value }))} style={{ ...inp, width: 80 }} /></td>
        <td><input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} style={{ ...inp, width: '100%' }} /></td>
        <td>
          <div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-secondary btn-sm" onClick={() => setEditing(false)} disabled={saving}>Cancel</button>
              <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>{saving ? 'Saving...' : 'Save'}</button>
            </div>
            {error && <div style={{ color: 'var(--danger)', fontSize: 11, marginTop: 4, maxWidth: 220 }}>{error}</div>}
          </div>
        </td>
      </tr>
    );
  }

  return (
    <tr>
      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{formatDate(row.log_date)}</td>
      <td>{row.staff_name}</td>
      <td>{row.project_title || '—'}</td>
      <td>{row.work_centre_name}</td>
      <td><strong>{row.hours_decimal}</strong></td>
      <td style={{ color: 'var(--text-muted)', fontSize: 12 }}>{row.notes || '—'}</td>
      <td>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-secondary btn-sm" onClick={startEdit}>Edit</button>
          <button className="btn btn-danger btn-sm" onClick={del}>Delete</button>
        </div>
      </td>
    </tr>
  );
}

function ManualEntriesTab({ projects, staff, workCentres }) {
  const dr = defaultRange();
  const [from, setFrom] = useState(dr.from);
  const [to, setTo] = useState(dr.to);
  const [staffFilter, setStaffFilter] = useState('');
  const [projFilter, setProjFilter] = useState('');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ date_from: from, date_to: to, limit: 200 });
    if (staffFilter) p.set('staff_id', staffFilter);
    if (projFilter) p.set('project_id', projFilter);
    api.get(`/production-logs?${p}`)
      .then(r => { setRows(r.data.value || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [from, to, staffFilter, projFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 20 }}>
        <DateRange from={from} to={to} onFrom={setFrom} onTo={setTo} />
        <select value={staffFilter} onChange={e => setStaffFilter(e.target.value)} style={inp}>
          <option value="">All Staff</option>
          {staff.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        <select value={projFilter} onChange={e => setProjFilter(e.target.value)} style={inp}>
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.job_number} — {p.title}</option>)}
        </select>
      </div>
      <div className="card">
        {loading ? (
          <div className="loading">Loading…</div>
        ) : !rows.length ? (
          <div className="empty-state">No entries for the selected filters</div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  <th>Date</th><th>Staff</th><th>Project</th><th>Work Centre</th>
                  <th>Hours</th><th>Notes</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.map(row => (
                  <ManualLogRow key={row.id} row={row} projects={projects} staff={staff} workCentres={workCentres} onSaved={load} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Time Logs page ─────────────────────────────────────────────────────────
export default function TimeLogs() {
  const [tab, setTab] = useState('clock');
  const [projects, setProjects] = useState([]);
  const [allStaff, setAllStaff] = useState([]);
  const [workCentres, setWorkCentres] = useState([]);

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data.value || []));
    api.get('/staff/all').then(r => setAllStaff(r.data.value || []));
    api.get('/work-centres').then(r => setWorkCentres(r.data.value || []));
  }, []);

  const tabs = [
    { id: 'clock', label: '⏱ Clock Sessions' },
    { id: 'manual', label: '✎ Manual Entries' },
  ];

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">TIME <span>LOGS</span></h2>
      </div>

      <div style={{ display: 'flex', gap: 8, marginBottom: 28, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{
            padding: '9px 16px', borderRadius: 4, cursor: 'pointer', transition: 'all 0.15s', whiteSpace: 'nowrap',
            border: tab === t.id ? '1px solid var(--accent)' : '1px solid var(--border)',
            background: tab === t.id ? 'var(--accent-glow)' : 'var(--bg-card)',
            color: tab === t.id ? 'var(--accent)' : 'var(--text-secondary)',
            fontFamily: 'var(--font-display)', fontSize: 13, fontWeight: 600,
            letterSpacing: 1, textTransform: 'uppercase',
          }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'clock' && <ClockSessionsTab projects={projects} staff={allStaff} />}
      {tab === 'manual' && <ManualEntriesTab projects={projects} staff={allStaff} workCentres={workCentres} />}
    </div>
  );
}
